import useAppSelector from '@/features/app/hooks/useAppSelector';
import useCanClientUseCookieTokenStorage from '@/features/auth/hooks/useCanClientUseCookieTokenStorage';
import useUnauthenticatedAxios from '@/shared/hooks/useAuthenticatedAxios';
import { useMutation } from '@tanstack/react-query';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';

export default function useRefreshAccessToken() {
  const featureFlags = useFeatureFlags();
  const token = useAppSelector((state) => state.auth.token);
  const axios = useUnauthenticatedAxios();
  const canClientUseCookieStorage = useCanClientUseCookieTokenStorage();

  return useMutation({
    retry: 2,
    mutationFn: async (
      preloadedLegacyToken?: string
    ): Promise<{
      accessToken?: string;
      accessTokenExpiresIn?: number;
      accessTokenExpiresAt?: number;
      refreshToken?: string;
      refreshTokenExpiresAt?: number;
      refreshTokenExpiresIn?: number;
    }> => {
      if (canClientUseCookieStorage) {
        return axios<{
          accessToken?: string;
          accessTokenExpiresIn?: number;
          accessTokenExpiresAt?: number;
          refreshToken?: string;
          refreshTokenExpiresAt?: number;
          refreshTokenExpiresIn?: number;
        }>({
          url: '/api/v1/access-token/refresh',
          method: 'POST',
          withCredentials: true,
        }).then((res) => {
          return {
            accessTokenExpiresAt: res.data.accessTokenExpiresAt,
            refreshTokenExpiresAt: res.data.refreshTokenExpiresAt,
          };
        });
      } else if (
        !canClientUseCookieStorage &&
        featureFlags.auth.useLegacyTokenStorage
      ) {
        return axios<{
          accessToken?: string;
          accessTokenExpiresIn?: number;
          accessTokenExpiresAt?: number;
          refreshToken?: string;
          refreshTokenExpiresAt?: number;
          refreshTokenExpiresIn?: number;
        }>({
          url: '/api/v1/access-token/refresh',
          method: 'POST',
          data: {
            refreshToken: preloadedLegacyToken ?? token!.refreshToken,
          },
          withCredentials: featureFlags.auth.useCookieTokenStorage,
        }).then((res) => {
          return {
            accessToken: res.data.accessToken,
            accessTokenExpiresIn: res.data.accessTokenExpiresIn,
            refreshToken: res.data.refreshToken,
            refreshTokenExpiresIn: res.data.refreshTokenExpiresIn,
          };
        });
      }
      return Promise.reject('NO SUPPORTED AUTHENTICATION MECHANISM');
    },
  });
}
